// src/config/subscriptionConfig.js

// =====================================================
// SUBSCRIPTION CONFIGURATION
// =====================================================
// Edit this file to adjust tiers, limits, and features
// Everything is controlled from here
// =====================================================

// MASTER KILL SWITCH
// Set to true to enforce paid tiers
// Set to false to give everyone unlimited access (for testing/beta)
export const BILLING_ENABLED = false;

// Beta period settings
export const BETA_CONFIG = {
  isBeta: true,
  betaEndDate: '2025-06-01', // Adjust when you're ready to launch
  earlyAdopterDiscount: 50, // Percentage off for beta users
};

// =====================================================
// TIER DEFINITIONS
// =====================================================
export const TIERS = {
  free: {
    id: 'free',
    name: 'Free',
    price: 0,
    priceYearly: 0,
    description: 'For bands getting started',
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    price: 8, // $/month
    priceYearly: 72, // $/year (25% savings)
    description: 'Unlimited everything for serious bands',
  },
};

// =====================================================
// FEATURE LIMITS BY TIER
// =====================================================
// Use Infinity for unlimited
// Use true/false for feature access
// Use numbers for quantity limits
//
// FREE: Good enough to try everything, limited quantities
// PRO: Unlimited everything + premium features
// =====================================================
export const FEATURE_LIMITS = {
  // Quantity limits
  shows: {
    free: 15,
    pro: Infinity,
  },
  bandMembers: {
    free: 2,
    pro: Infinity,
  },
  inventoryItems: {
    free: 20,
    pro: Infinity,
  },
  documents: {
    free: 10,
    pro: Infinity,
  },
  activePolls: {
    free: 3,
    pro: Infinity,
  },
  contacts: {
    free: 10,
    pro: Infinity,
  },
  expenses: {
    free: 20,
    pro: Infinity,
  },

  // Feature access (true/false)
  salesMode: {
    free: false,
    pro: true,
  },
  exportData: {
    free: false,
    pro: true,
  },
  bandsintownImport: {
    free: false,
    pro: true,
  },
  showMerchBreakdown: {
    free: false,
    pro: true,
  },
  
  // Features available to everyone
  calendar: {
    free: true,
    pro: true,
  },
  timeline: {
    free: true,
    pro: true,
  },
  customDashboard: {
    free: true,
    pro: true,
  },
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Get the limit for a feature based on tier
 * Returns Infinity if billing is disabled (everything unlimited)
 */
export const getFeatureLimit = (feature, tier = 'free') => {
  if (!BILLING_ENABLED) {
    // Kill switch is off - everything unlimited
    const featureConfig = FEATURE_LIMITS[feature];
    if (!featureConfig) return true;
    
    // Return the highest tier value (usually Infinity or true)
    if (typeof featureConfig.pro === 'boolean') return true;
    return Infinity;
  }
  
  const featureConfig = FEATURE_LIMITS[feature];
  if (!featureConfig) {
    console.warn(`Unknown feature: ${feature}`);
    return true;
  }
  
  return featureConfig[tier] ?? featureConfig.free;
};

/**
 * Check if a feature is accessible for a tier
 * Returns true if billing is disabled
 */
export const hasFeatureAccess = (feature, tier = 'free') => {
  if (!BILLING_ENABLED) return true;
  
  const limit = getFeatureLimit(feature, tier);
  
  // Boolean features
  if (typeof limit === 'boolean') return limit;
  
  // Numeric limits - having any limit means access
  return limit > 0;
};

/**
 * Check if user is within their usage limit
 */
export const isWithinLimit = (feature, currentUsage, tier = 'free') => {
  if (!BILLING_ENABLED) return true;
  
  const limit = getFeatureLimit(feature, tier);
  
  if (limit === Infinity) return true;
  if (typeof limit === 'boolean') return limit;
  
  return currentUsage < limit;
};

/**
 * Get tier info
 */
export const getTierInfo = (tierId) => {
  return TIERS[tierId] || TIERS.free;
};

/**
 * Get all features and their limits for a tier
 */
export const getTierFeatures = (tier = 'free') => {
  const features = {};
  
  Object.keys(FEATURE_LIMITS).forEach(feature => {
    features[feature] = BILLING_ENABLED 
      ? FEATURE_LIMITS[feature][tier]
      : (typeof FEATURE_LIMITS[feature].pro === 'boolean' ? true : Infinity);
  });
  
  return features;
};